import Vue from 'vue';
import VueRouter from 'vue-router';
import ApiService from '@jobstairs/axios-api-service';

const Login = () => import('../pages/LoginPage');
const Error = () => import('../pages/error/ErrorPage.vue');
const JobAdNotFound = () => import('../pages/error/JobAdNotFound');
const JobAdAnalysis = () => import('../pages/JobAdAnalysis');
const JobAdOptimisation = () => import('../pages/JobAdOptimisation');
const CompanyAnalysis = () => import('../pages/CompanyAnalysis');
const BaDefaultLayout = () => import('../components/layout/BaDefaultLayout');
const BaLoginLayout = () => import('../components/layout/BaLoginLayout');
const QuickCheck = () => import('../pages/QuickCheck.vue');
const Help = () => import('../pages/HelpPage.vue');
const PivacyPolicy = () => import('../pages/PrivacyPolicyPage.vue');
const Imprint = () => import('../pages/ImprintPage.vue');

Vue.use(VueRouter);

export const Routes = {
    JOB_AD_ANALYSIS: 'JobAdAnalysis',
    JOB_AD_ANALYSIS_EXTERNAL_ID: 'JobAdAnalysisExternalId',
    JOB_AD_OPTIMISATION: 'JobAdOptimisation',
    JOB_AD_NOT_FOUND: 'JobAdNotFound',
    COMPANY_ANALYSIS: 'CompanyAnalysis',
    LOGIN: 'Login',
    CUSTOM_JOB_AD_ANALYSIS: 'CustomJobAdAnalysis',
    CUSTOM_JOB_AD_OPTIMISATION: 'CustomJobAdOptimisation',
    QUICK_CHECK: 'QuickCheck',
    HELP: 'Help',
    IMPRINT: 'Imprint',
    PRIVACY_POLICY: 'PrivacyPolicy',
    BEESITE_ID_JOB_AD_ANALYSIS: 'BeesiteJobAdAnalysis',
    BEESITE_ID_JOB_AD_OPTIMISATION: 'BeesiteJobAdOptimisation',
};

const setupRouter = (config) =>
    new VueRouter({
        routes: [
            {
                path: '/job-ad-analysis/:id',
                name: Routes.JOB_AD_ANALYSIS,
                component: JobAdAnalysis,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/quick-check',
                name: Routes.QUICK_CHECK,
                component: QuickCheck,
                meta: {
                    layout: BaDefaultLayout,
                    requiresAuth: true,
                },
            },
            {
                path: '/job-ad-analysis/:id/optimize',
                name: Routes.JOB_AD_OPTIMISATION,
                component: JobAdOptimisation,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/job-ad-analysis/optimize',
                name: Routes.CUSTOM_JOB_AD_OPTIMISATION,
                component: JobAdOptimisation,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/job-ad-analysis-beesite/:id',
                name: Routes.BEESITE_ID_JOB_AD_ANALYSIS,
                component: JobAdAnalysis,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/job-ad-analysis-beesite/:id/optimize',
                name: Routes.BEESITE_ID_JOB_AD_OPTIMISATION,
                component: JobAdOptimisation,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            /**
             * Entrypoint for links from companies' external applications: the customers' TRMs don't know the
             * corresponding jobstairs id. They pass their company id and the external job ad id.
             * With the combination of these values we retrieve the internal jobstairs id and redirect to the
             * standard analysis page with the retrieved internal id:
             * /job-ad-analysis/[Company ID]/[Company's Job ID] -> /job-ad-analysis/[Jobstairs Job ID]
             * Redirects to a Not Found page when no matching job ad could be found.
             */
            {
                path: '/job-ad-analysis/:companyId/:jobId',
                name: Routes.JOB_AD_ANALYSIS_EXTERNAL_ID,
                beforeEnter: async (to, from, next) => {
                    try {
                        const jobId = await convertExternalToInternalId(
                            {
                                jobId: to.params.jobId,
                                companyId: to.params.companyId,
                            },
                            config,
                        );
                        next({
                            name: Routes.JOB_AD_ANALYSIS,
                            params: { id: jobId },
                        });
                    } catch {
                        next({
                            name: Routes.JOB_AD_NOT_FOUND,
                        });
                    }
                },
            },
            {
                path: '/job-ad-analysis',
                name: Routes.CUSTOM_JOB_AD_ANALYSIS,
                component: JobAdAnalysis,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/job-ad-not-found',
                name: Routes.JOB_AD_NOT_FOUND,
                component: JobAdNotFound,
                meta: {
                    layout: BaDefaultLayout,
                    requiresAuth: false,
                },
            },
            {
                path: '/company-analysis',
                name: Routes.COMPANY_ANALYSIS,
                component: CompanyAnalysis,
                meta: {
                    layout: BaDefaultLayout,
                    dark: true,
                    requiresAuth: true,
                },
            },
            {
                path: '/',
                name: Routes.HELP,
                component: Help,
                meta: { layout: BaDefaultLayout, requiresAuth: true },
            },
            {
                path: '/impressum',
                name: Routes.IMPRINT,
                component: Imprint,
                meta: { layout: BaDefaultLayout, requiresAuth: false },
            },
            {
                path: '/privacy-policy',
                name: Routes.PRIVACY_POLICY,
                component: PivacyPolicy,
                meta: { layout: BaDefaultLayout, requiresAuth: false },
            },
            {
                path: '/login',
                name: Routes.LOGIN,
                component: Login,
                meta: {
                    layout: BaLoginLayout,
                    requiresAuth: false,
                },
            },
            {
                path: '/*',
                component: Error,
                meta: {
                    layout: BaDefaultLayout,
                    requiresAuth: false,
                },
            },
        ],
        mode: process.env.VUE_APP_ROUTER_MODE || 'hash',
    });

async function convertExternalToInternalId({ jobId, companyId }, config) {
    const dataApiService = new ApiService({
        baseURL: config.search.baseURL,
    });

    const mapRequestData = config.search.hydrator.request;
    const mapResponseData = config.search.hydrator.response;

    const externalIdProp = config.jobAd.externalIdProperty;
    const idProp = config.jobAd.idProperty;
    const companyProp = config.jobAd.companyProperty;

    const requestData = mapRequestData(
        {
            properties: [idProp, externalIdProp],
        },
        {
            [externalIdProp]: jobId,
            [companyProp]: companyId,
        },
    );
    const response = await dataApiService.get({
        endpoint: config.search.endpoint,
        params: requestData,
    });

    const data = mapResponseData(response.data);
    const job = data?.items?.[0];

    if (job?.[externalIdProp] !== jobId) {
        throw new Error(
            `Could not convert ${jobId} for company ${companyId} to an internal job ad id.`,
        );
    }
    return job?.[idProp];
}

export default setupRouter;
